import { useCallback, useState } from 'react'
import type { BuildPartDto } from '../../app/models/build'
import type { ZoneRect } from './useZoneGeometry'
import { getPartDefaults } from './partDefaults'
import PartOverlayItem from './PartOverlayItem'

interface Props {
  buildParts: BuildPartDto[];
  zoneGeometry: Record<string, ZoneRect>;
  /** Container width/height in pixels — used to convert normalised coords to px. */
  containerWidth: number;
  containerHeight: number;
  platform: string | null | undefined;
  isOwner: boolean;
  activeZone: string | null;
  onPartPositionChange: (buildPartId: string, x: number, y: number, zoneId?: string) => void;
  onPartPositionReset: (buildPartId: string) => void;
}

/**
 * Absolute-positioned overlay rendered on top of PlatformSchematic.
 * Each BuildPart gets a draggable icon pinned at its saved or default position.
 * Supports zone-aware positioning with visual feedback for zone boundaries.
 */
export default function PartOverlayLayer ({
  buildParts,
  zoneGeometry,
  containerWidth,
  containerHeight,
  platform,
  isOwner,
  activeZone,
  onPartPositionChange,
  onPartPositionReset
}: Props) {
  const [draggingPartId, setDraggingPartId] = useState<string | null>(null)
  const [hoverZone, setHoverZone] = useState<string | null>(null)

  // Convert container-fraction coords → pixel coords.
  const toPixels = useCallback(
    (xFrac: number, yFrac: number) => ({
      x: xFrac * containerWidth,
      y: yFrac * containerHeight
    }),
    [containerWidth, containerHeight]
  )

  // Convert pixel coords back to container-fraction coords.
  const fromPixels = useCallback(
    (px: number, py: number) => ({
      x: Math.max(0, Math.min(1, px / containerWidth)),
      y: Math.max(0, Math.min(1, py / containerHeight))
    }),
    [containerWidth, containerHeight]
  )

  // Detect which zone contains a pixel point
  const getZoneAtPoint = useCallback(
    (px: number, py: number): string | null => {
      for (const [zoneName, rect] of Object.entries(zoneGeometry)) {
        if (
          px >= rect.x &&
          px <= rect.x + rect.width &&
          py >= rect.y &&
          py <= rect.y + rect.height
        ) {
          return zoneName
        }
      }
      return null
    },
    [zoneGeometry]
  )

  // Get zone-relative coordinates within a zone
  const getZoneRelativeCoords = useCallback(
    (px: number, py: number, zoneId: string): { x: number; y: number } => {
      const zone = zoneGeometry[zoneId]
      if (!zone) return { x: 0.5, y: 0.5 }
      return {
        x: Math.max(0, Math.min(1, (px - zone.x) / zone.width)),
        y: Math.max(0, Math.min(1, (py - zone.y) / zone.height))
      }
    },
    [zoneGeometry]
  )

  if (containerWidth === 0 || Object.keys(zoneGeometry).length === 0) return null

  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 5
      }}
    >
      {/* Zone boundary visualization during drag */}
      {draggingPartId && hoverZone && zoneGeometry[hoverZone] && (
        <div
          style={{
            position: 'absolute',
            left: `${(zoneGeometry[hoverZone].x / containerWidth) * 100}%`,
            top: `${(zoneGeometry[hoverZone].y / containerHeight) * 100}%`,
            width: `${(zoneGeometry[hoverZone].width / containerWidth) * 100}%`,
            height: `${(zoneGeometry[hoverZone].height / containerHeight) * 100}%`,
            border: '2px dashed var(--gs-accent)',
            borderRadius: 4,
            backgroundColor: 'rgba(var(--gs-accent-rgb, 255, 200, 0), 0.08)',
            pointerEvents: 'none',
            transition: 'all 0.15s ease'
          }}
        />
      )}

      {buildParts.map((bp) => {
        const zone = bp.zoneOverride ?? bp.part?.category ?? 'Other'
        const category = bp.part?.category ?? 'Other'
        const defaults = getPartDefaults(platform, category)

        // Use zone-relative position if available, otherwise fall back to legacy position
        let position
        if (bp.zoneId && bp.zoneRelativeXPercent !== undefined && bp.zoneRelativeYPercent !== undefined) {
          const zoneRect = zoneGeometry[bp.zoneId]
          if (zoneRect) {
            const px = zoneRect.x + (bp.zoneRelativeXPercent * zoneRect.width)
            const py = zoneRect.y + (bp.zoneRelativeYPercent * zoneRect.height)
            position = { x: px, y: py }
          } else {
            const xFrac = bp.xOffsetPercent ?? defaults.position.x
            const yFrac = bp.yOffsetPercent ?? defaults.position.y
            position = toPixels(xFrac, yFrac)
          }
        } else {
          const xFrac = bp.xOffsetPercent ?? defaults.position.x
          const yFrac = bp.yOffsetPercent ?? defaults.position.y
          position = toPixels(xFrac, yFrac)
        }

        const handleDragStart = () => {
          setDraggingPartId(bp.id)
          setHoverZone(zone)
        }

        const handleDragMove = (px: number, py: number) => {
          const detectedZone = getZoneAtPoint(px, py)
          setHoverZone(detectedZone)
        }

        const handleDragEnd = (px: number, py: number) => {
          const detectedZone = getZoneAtPoint(px, py)
          if (detectedZone) {
            const zoneRelCoords = getZoneRelativeCoords(px, py, detectedZone)
            onPartPositionChange(bp.id, zoneRelCoords.x, zoneRelCoords.y, detectedZone)
          } else {
            const frac = fromPixels(px, py)
            onPartPositionChange(bp.id, frac.x, frac.y)
          }
          setDraggingPartId(null)
          setHoverZone(null)
        }

        return (
          <PartOverlayItem
            key={bp.id}
            buildPart={bp}
            position={position}
            size={defaults.size}
            isOwner={isOwner}
            isActiveZone={zone === activeZone}
            isDragging={draggingPartId === bp.id}
            onDragStart={handleDragStart}
            onDragMove={handleDragMove}
            onDragEnd={handleDragEnd}
            onResetPosition={() => onPartPositionReset(bp.id)}
          />
        )
      })}
    </div>
  )
}
